<template>
	<div class="form-group">
		<div class="d-flex justify-content-between">
			<span class="text-primary">{{ $t('Size And Position') }}</span>
			<!-- Ratio Toggle -->
			<div class="d-flex">
				<div class="mt-0">
					<i
					class="md-18 pointer mr-2 pt-1px"
					:value="isPosition"
					:class="{ 'material-icons': isPosition, 'material-icons ratio-locked-disabled': !isPosition }"
					v-tooltip="isPosition ? $t('Position Locked') : $t('Position Not Locked')"
					@click="togglePositionLock">
						lock
					</i>
				</div>
				<div class="mt-0">
					<i
					class="md-18 pointer mr-2 pt-1px"
					:value="isRatio"
					:class="{ 'text-white': isRatio, 'dis-icon-bg': !isRatio }"
					v-tooltip="isRatio ? $t('Ratio Locked') : $t('Ratio Not Locked')"
					@click="toggleRatioLock">
						<aspect-ratio :size="19"></aspect-ratio>
					</i>
				</div>
				<span :class="{'disabled': !originalWidth}" v-if="type === 'image' || type === 'video' || type === 'lottie'" class="mt-0 pt-0 pointer pt-0 mr-4px" @click="rescale" v-tooltip="('Rescale')"><arrow-expand-all :size="19"></arrow-expand-all></span>
				<span v-if="type === 'lower_third'" class="mt-0 pt-0 pointer pt-0 mr-4px" @click="rescale" v-tooltip="('Rescale')"><arrow-expand-all :size="19"></arrow-expand-all></span>
				<span :class="{'disabled': (!originalWidth && type === 'image') || (!originalWidth && type === 'video')}" class="mt-0 pt-0 pointer pt-0" v-tooltip="('Reset')"><undo-variant @click="reset" :size="19"></undo-variant></span>
			</div>
		</div>
		<div class="row row-thin position-relative" :class="{'disabled': isPosition}">
			<!-- Width -->
			<div class="col-md-6 pr-1">
				<custom-input
					:disabled="isPosition"
					class="mb-3"
					type="number"
					:label="$t('Width')"
					:suffix="'px'"
					:value="width"
					:min="75"
					:max="8000"
					:step="1"
					@input="onInputChange('width', ...arguments)"
					@keyenter="onInputEnter('width', ...arguments)"
					@blur="onBlur"
				/>
			</div>
			<!-- Height -->
			<div class="col-md-6 pl-1">
				<custom-input
					:disabled="isPosition"
					class="mb-3"
					type="number"
					:label="$t('Height')"
					:suffix="'px'"
					:value="height"
					:min="minHeight"
					:max="8000"
					:step="1"
					@input="onInputChange('height', ...arguments)"
					@keyenter="onInputEnter('height', ...arguments)"
					@blur="onBlur"
				/>
			</div>
		</div>
		<div class="row row-thin">
			<!-- Position -->
			<div class="col-md-6 pr-1 w-100" :class="{'disabled': isPosition}">
				<md-menu md-align-trigger md-direction="bottom-start" class="w-100">
					<div md-menu-trigger>
						<custom-input
							:disabled="isPosition"
							type="select"
							class="form-control cur-p relative w-100"
							:label="$t('Position')"
							:value="position"
							@input="onInputChange('position', ...arguments)"
						/>
					</div>
					<md-menu-content id="scrolls-style">
						<md-menu-item class="item-property-rundown w-100" v-for="item in positions" @click="onInputChange('position', item.key)"
						:key="item.key">{{item.label}}</md-menu-item>
					</md-menu-content>
				</md-menu>
			</div>
			<!-- Coordinates -->
			<div class="col-md-6 pl-1" :class="{'disabled': isPosition}">
				<div class="d-flex">
					<custom-input
						class="mb-3 mr-2"
						type="number"
						:label="$t('X')"
						:suffix="'px'"
						:value="x"
						:min="-2000"
						:max="8000"
						:step="1"
						:disabled="position !== 'custom' || isPosition"
						key="input-x"
						@input="onInputChange('x', ...arguments)"
						@keyenter="onInputEnter('x', ...arguments)"
						:allowNegative="true"
						@blur="onBlur"
					/>
					<custom-input
						class="mb-3"
						type="number"
						:label="$t('Y')"
						:suffix="'px'"
						:value="y"
						:min="-2000"
						:max="8000"
						:step="1"
						:disabled="position !== 'custom' || isPosition"
						key="input-y"
						@input="onInputChange('y', ...arguments)"
						@keyenter="onInputEnter('y', ...arguments)"
						:allowNegative="true"
						@blur="onBlur"
					/>
				</div>
			</div>
			<div class="col-md-12 w-100" v-if="isBlendActive">
				<md-menu md-align-trigger md-direction="bottom-start" class="w-100">
					<div md-menu-trigger>
						<custom-input
							type="select"
							class="form-control cur-p relative"
							:label="$t('Blending Mode')"
							:value="blendmode"
							@input="onInputChange('blendmode', ...arguments)"
						/>
					</div>
					<md-menu-content id="scrolls-style">
						<md-menu-item class="item-property-rundown" v-for="item in blendModes" @click="onInputChange('blendmode', item.key)" :key="item.key">{{ item.label }}</md-menu-item>
				</md-menu-content>
				</md-menu>
			</div>
		</div>
	</div>
</template>

<script>
import UndoVariant from 'vue-material-design-icons/UndoVariant.vue';
import AspectRatio from 'vue-material-design-icons/AspectRatio.vue';
import ArrowExpandAll from 'vue-material-design-icons/ArrowExpandAll.vue';
import {
	positionOptions,
	blendModeOptions,
} from '@/lib/statics';
import {
} from '@/lib/helper';
import CustomInput from '@/components/CustomInput.vue';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
	name: 'PositionSettings',
	props: {
		position: {
			type: String,
			default: 'default',
		},
		blendmode: {
			type: String,
			default: 'normal',
		},
		ratioWidth: {
			default: () => 16,
		},
		ratioHeight: {
			default: () => 9,
		},
		width: {
		},
		height: {
		},
		x: {
		},
		y: {
		},
		originalWidth: {
		},
		originalHeight: {
		},
		isLowerThird: {
			type: Boolean,
		},
		isRatio: {
			type: Boolean,
		},
		isPosition: {
			type: Boolean,
		},
		type: {
			type: String,
		},
		isBlendActive: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		CustomInput,
		UndoVariant,
		ArrowExpandAll,
		AspectRatio,
	},
	data() {
		return {
			positionOptions,
			blendModeOptions,
			selectedPosition: null,
			selectedBlendMode: null,
			isRatioLocked: true,
			isPositionLocked: false,
			ratio: 0.5625,
		};
	},
	methods: {
		initData() {
			this.setRatio();
			const selectedPosition = this.positionOptions.find(({ key }) => key === this.position);
			const selectedBlendMode = this.blendModeOptions.find(({ key }) => key === this.blendmode);
			if (selectedBlendMode) {
				this.selectedBlendMode = selectedBlendMode;
			}
			if (selectedPosition) {
				this.selectedPosition = selectedPosition;
			}
		},
		onChange() {
			this.$emit('change');
		},
		onSave() {
			this.$emit('save');
		},
		onEnter() {
			this.$emit('keyenter');
		},
		onBlur() {
			this.$emit('save');
		},
		// onPositionChange(e) {
		// 	const value = e.target.value;
		// 	this.$emit('update:position', value);
		// 	this.onChange();
		// },
		onInputEnter(prop, value) {
			this.$emit(`update:${prop}`, value);
			// const value = e.target.value;
			if ((prop === 'width' || prop === 'height') && this.isRatio) {
				if (prop === 'width') {
					const height = value * this.ratio;
					this.$emit('update:height', height);
				} else {
					const width = value / this.ratio;
					this.$emit('update:width', width);
				}
			}
			if (prop === 'position') {
				if (value !== 'custom') {
					this.$emit('update:x', 0);
					this.$emit('update:y', 0);
				}
				this.$emit('updatePosition');
			}
			if (prop === 'blendmode') {
				this.$emit('updateBlendMode');
			}
			this.onEnter();
		},
		onInputChange(prop, value) {
			this.$emit(`update:${prop}`, value);
			// const value = e.target.value;
			if ((prop === 'width' || prop === 'height') && this.isRatio) {
				if (prop === 'width') {
					const height = value * this.ratio;
					this.$emit('update:height', height);
				} else {
					const width = value / this.ratio;
					this.$emit('update:width', width);
				}
			}
			if (prop === 'position') {
				if (value !== 'custom') {
					this.$emit('update:x', 0);
					this.$emit('update:y', 0);
				}
				this.$emit('updatePosition');
				return this.onSave();
			}
			if (prop === 'blendmode') {
				this.$emit('updateBlendMode');
				return this.onSave();
			}
			this.onChange();
			return '';
		},
		toggleRatioLock() {
			const isRatioLocked = !this.isRatio;
			this.$emit('update:isRatio', isRatioLocked);
			if (this.isRatioLocked) {
				this.setRatio();
			}
			this.onSave();
		},
		togglePositionLock() {
			const isPositionLocked = !this.isPosition;
			this.$emit('update:isPosition', isPositionLocked);
			this.onSave();
		},
		setRatio() {
			const width = parseInt(this.width);
			const height = parseInt(this.height);
			let ratio = height / width;
			if (Number.isNaN(ratio)) {
				const defaultRatio = this.ratioHeight / this.ratioWidth;
				ratio = defaultRatio;
			}
			this.ratio = ratio;
		},
		reset() {
			// 	this.originalHeight);
			this.$emit('reset');
		},
		rescale() {
			// 	this.originalHeight);
			this.$emit('rescale');
		},
	},
	computed: {
		positions() {
			// if (this.isLowerThird) {
			// 	return positionOptionsLT;
			// }
			return positionOptions;
		},
		blendModes() {
			return blendModeOptions;
		},
		minHeight() {
			if (!this.isRatio) return 75;
			return 42;
		},
	},
	watch: {
		originalWidth() {
			this.setRatio();
		},
		originalHeight() {
			this.setRatio();
		},

	},
	created() {
		// this.$nextTick(() => {
		this.initData();
		// });
	},

};
</script>
